import { Injectable } from '@angular/core';
import { AccountsService } from './accounts.service';
import { AdminService } from './admin.service';
import { gatewayMessages } from '../constants/constants';
import { TranslateService } from '@ngx-translate/core';
import { CountryStateService } from './country-state.service';
import { Subject } from 'rxjs';

import { Account, Site} from '@mitel/cloudlink-sdk';
import { GatewayProgressService, ProgressItem } from './gateway-progress.service' ;
import { ErrorsHandlerService } from './errors-handler.service';
import { pbxStatus, commonErrorMessages, gatewayConnectStateMessages, commonMessages, gatewayErrorMessages } from '../constants/constants';
import { TunnelService } from '@mitel/cloudlink-sdk';
import { AuthService } from './auth.service';
import { GatewayEchoService } from './gateway-echo.service';
import { ClHeaderComponent } from '@mitel/cloudlink-console-components';
import { IntegrationHighLevelStatus } from '../interfaces/integrations';
import { AppStateService } from './app-state.service'; 
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class GatewayStatusService {
  site: Site;
  lowLevelStatus = null;
  getStatusFlag = true;
  lowLevelFlag: boolean;
  readOnlyMode: boolean = false;
  
  constructor(
    private adminSvc: AdminService,
    private accountSvc: AccountsService,
    private translateService: TranslateService,
    private countryStateService: CountryStateService,
    private gatewayProgressSvc: GatewayProgressService,
    private errorHandlerSvc: ErrorsHandlerService,
    private tunnelSvc: TunnelService,
    private authSvc: AuthService,
    private echoSvc: GatewayEchoService,
    private clHeader: ClHeaderComponent,
    private appStateService: AppStateService
  ) { }

  /* 
  For the Gateway integration, we go through the same process to determine the the HighLevelStatus (Account List page) vs the LowLevelStatus (Account Edit page).
  We will do the same checks for both (tags, connection, system version, etc).
  But for the LowLevelStatus we need to determine what to show on the button. Also we sometimes include the step same in the status message
  */
  
  /*
  The HighLevelStatus is for the Account List page.
  Will likely be called multiple times when the page is loading - once for each account with the Gateway integration
  */
  async getHighLevelStatus(gatewayDetected: boolean, account: Account, haltStatusUpdateSubject: Subject<boolean>, tags?: any) {
    haltStatusUpdateSubject.subscribe( halt => {
      // console.log("I NEED TO HALT STATUS COLLECTION NOW", halt);
      if (halt) {
        this.getStatusFlag = false;
      } else {
        this.getStatusFlag = true;
      }   
      //set flag here and then check flag at each step in the status gathering
    });
    this.lowLevelFlag = false;
    if (this.getStatusFlag) return this.getStatus(gatewayDetected, account, this.lowLevelFlag, tags);
  }

  /*
  Should be called once when the Account Edit page loads and Gateway integration is toggled ON.
  */ 
  async getLowLevelStatus(gatewayDetected: boolean, account: Account){
    this.site = null; //we'll use the site for the country warning
    this.lowLevelFlag = true;
    if(this.appStateService.getSupportPortalMode()){
      this.readOnlyMode = true;
    }
    return this.getStatus(gatewayDetected, account, this.lowLevelFlag);
  }


  /*
  Whether HighLevel or LowLevel, the proces to determine the status is nearly the same.
  For the LowLevel we need to also get a status for the button and we need to save the status
  details to a class variable so they can be retrieved later.
  */
  async getStatus(gatewayDetected: boolean, account: Account, lowLevelFlag: boolean, tags?: any){
    if (lowLevelFlag){ 
      this.lowLevelStatus = null;
    }
    let statusData = {};
    let accountTags;

    if (!tags && (this.getStatusFlag || lowLevelFlag)) {
      accountTags = await this.getTagStatus(account.accountId);
    } else if (tags) {
      accountTags = tags;
    }

    if(this.readOnlyMode){
      statusData = this.setButtonStatus(false, accountTags);
      statusData['statusMessage'] = commonMessages.READONLY_STATUS_UNAVAILABLE,
      statusData['isButtonDisabled'] = true,
      statusData['integrationHighLevelStatus'] = {state: 2, statusDisplayString: ''}
      return this.formatStatus(statusData, lowLevelFlag);
    }

    if (this.getStatusFlag && lowLevelFlag){
      // If there are no account tags or there are account tags but they have never been onboarded (so don't have onboard tag)
      // then create onboarding tag to step 2 (they have already set up the account information), so they will ultimately
      // not see the customer page of the GWP
      if (this.getStatusFlag && (!accountTags || (accountTags && !accountTags['on-board-progress']))) {
        try {
          const tagParams: any = {};
          tagParams['on-board-progress'] = { 'step': 1, 'name': 'account', 'succeeded': true };
          accountTags = await this.adminSvc.tryUpdateAccountTags(account.accountId, accountTags, tagParams);
          console.log('first set up of gateway tags:', accountTags);
        } catch(err){
          console.error('failed to create on-board-progress tag', JSON.stringify(err));
        }
      }
      if (accountTags && accountTags['on-board-progress']) {
        statusData = this.setButtonStatus(gatewayDetected, accountTags);
      }
    }

    if (this.getStatusFlag && accountTags && accountTags['on-board-progress']) {
      statusData = await this.determineStatus(gatewayDetected, account, accountTags, statusData, lowLevelFlag);
    }

    if(!statusData || Object.keys(statusData).length === 0){
      return null;
    } else {
      return this.formatStatus(statusData, lowLevelFlag);
    }
  }


  /* 
  Formats the statusData depeding on if we want the HighLevelStatus or the LowLevelStatus
  Might need to come back here to think deeper about edge cases ('unknown')
  */
  formatStatus(statusData, lowLevelFlag: boolean) {
    if (!statusData || Object.keys(statusData).length === 0 || !("integrationHighLevelStatus" in statusData) || !("statusMessage" in statusData)) {
      return undefined;
    }

    if(statusData.statusMessage === gatewayMessages.OVERVIEW){
      statusData.integrationHighLevelStatus.state = 1; //the only Good status will be Overview - onboarding is done
    } else if (statusData.integrationHighLevelStatus.state === 0 && statusData.statusMessage !== '') {
      //There is a message. Since the state was not already set 1(GOOD), 2(INFO) or 3(ERROR) - we'll put it as 2
      statusData.integrationHighLevelStatus.state = 2;
    } else if(statusData.statusMessage === '') {
      statusData.statusMessage = commonMessages.UNKNOWN_STATUS;
      statusData.integrationHighLevelStatus.state = 3; 
    }

    statusData.integrationHighLevelStatus.statusDisplayString = this.translateService.instant(statusData.statusMessage);
    
    if (lowLevelFlag) {
      this.lowLevelStatus = {
        statusMessage: this.getStatusMsgWithStepName(statusData), 
        buttonMessage: statusData.buttonMessage && statusData.buttonMessage !== '' ? this.translateService.instant(statusData.buttonMessage) :'',
        isButtonDisabled: statusData.isButtonDisabled,
        integrationHighLevelStatus: statusData.integrationHighLevelStatus
      }
      return this.lowLevelStatus;
    } else {
      return {integrationHighLevelStatus: statusData.integrationHighLevelStatus};
    }
  }

  /* 
  In some cases in the GWP Partner Dashboard, we show the step name as well as a statusMessage
  This more descriptive msg will only be shown on the Account Edit page (LowLevelStatus only)
  */
  getStatusMsgWithStepName(statusData) {
    if(statusData.statusMessage !== '' && statusData.showStep && statusData.stepName !== ''){
        return this.translateService.instant('gwpIntegration.stepName.' + statusData.stepName) + ": " + this.translateService.instant(statusData.statusMessage);
    } else if(statusData.statusMessage !== '') {
        return this.translateService.instant(statusData.statusMessage);
    } else {
        return '';
    }
  }
  
  setButtonStatus(gatewayDetected, accountTags) {
    let statusData = { 
      isButtonDisabled: true,
      buttonMessage: null
    };
    if (accountTags && accountTags['on-board-progress'] && accountTags['on-board-progress'].step >= 1) {
      if (gatewayDetected) {
        statusData.isButtonDisabled = false;
      }
      statusData.buttonMessage = gatewayMessages.ADD_GATEWAY;
      if (accountTags['on-board-progress'].step === 5) {
        statusData.isButtonDisabled = false;
        statusData.buttonMessage = gatewayMessages.OVERVIEW;
      } else if(accountTags['on-board-progress'].step > 1) {
        statusData.isButtonDisabled = false;
        statusData.buttonMessage = gatewayMessages.CONTINUE;
      }
    }
    return statusData;
  }

  async getTagStatus(accountId: string) {
    return await this.accountSvc.getAccountTagsById(accountId);
  }

  /* **************************************************************************************************** */
  //None of these should be called from outside the service.

  /*
  Should always be called when there's an account and tags.
  Otherwise, it should do nothing.
  This method mainly comes from getAccountTagData in GWP.
  Really just added the part about showing NO_GATEWAY
  */
  async determineStatus(gatewayDetected, account, accountTags, statusData, lowLevelFlag: boolean) {
    statusData['integrationHighLevelStatus'] = {state: 0, statusDisplayString: ''};
    statusData['statusMessage'] = '';
    statusData['progressItemStatus'] = '';
    statusData['progressItem'] = null;
    statusData['stepName'] = '';
    statusData['showStep'] = false;

    const lastStep = accountTags['on-board-progress'].name;
    let lastStepSucceeded = accountTags['on-board-progress'].succeeded;
    if (lastStepSucceeded) {
      statusData.progressItem = this.gatewayProgressSvc.getProgressItem(lastStep);
      statusData.progressItemStatus = statusData.progressItem.status;
    } else {
      statusData.integrationHighLevelStatus.state = 3; //isError
      statusData.progressItem = this.gatewayProgressSvc.getPrevProgressItem(lastStep);
      statusData.stepName = lastStep;
      statusData.progressItemStatus = `${accountTags['on-board-progress'].reason}`;
    }
    // Get sites if progress step > 1
    if (statusData.progressItem.step > 1 || accountTags['gateway-connection']) {
      let site;
      let platform;
      let options;

      try
      {
        if (this.getStatusFlag) {
          site = await this.getSite(account);
          this.site = site; //only used for country warning on Account Edit page
        }
        if (this.getStatusFlag) {
          options = await this.getAssumeRoleToken( account, lowLevelFlag );
        }
        if (this.getStatusFlag) {
          let platformResult = await this.getPlatform(account, site, accountTags, statusData, lowLevelFlag, options);
          platform = platformResult[0];
          statusData = platformResult[1];
        }
      }
      catch (err)
      {
        // setting the status to unknown by setting the message to ''
        statusData.statusMessage = '';
        console.log("There was an error either requesting the site, token or platform", err);
      }
    } else if(!lastStepSucceeded && statusData.stepName === 'site') {
      statusData.statusMessage = statusData.progressItemStatus;
    } else if (statusData.progressItem.step <= 1 && !gatewayDetected) {
      statusData.statusMessage = gatewayMessages.NO_GATEWAY;
      statusData.integrationHighLevelStatus.state = 3; //error
    } else {
      statusData.statusMessage = statusData.progressItemStatus;
    }
    return statusData;
  }

  async getSite(account): Promise<Site> {
    let site = null;
    try {
      if (this.getStatusFlag) {
        const siteCollection = await this.adminSvc.getSites(account.accountId);
        if (siteCollection && siteCollection.count > 0 && siteCollection._embedded && siteCollection._embedded.items) {
          const sites = siteCollection._embedded.items;
          if (sites[0]) {
            site = sites[0];
          }
        }
      }
    } catch (reason) {
      this.errorHandlerSvc.handleError(reason);
      console.error('failed to get site', JSON.stringify(reason));
    }
    return site;
  }

  async getAssumeRoleToken( account, lowLevelFlag: boolean ): Promise<any>
  {
    let options;
    if (this.getStatusFlag) {
      try {
        options = await this.authSvc.getAssumeRoleHeader(account.accountId);
        return options;
      } catch (error) {
        // This is going to take care of 401 error from auth service.
        if(lowLevelFlag){
          this.errorHandlerSvc.handleError(error);
        }
        // Throwing it again to get it caught by outer catch to print the failure message.
        throw error;
      }
    }
  }

  async getPlatform(account, site, accountTags, statusData, lowLevelFlag: boolean, options) {
    let platform = null;
    this.accountSvc.setPlatform(null);
    if (account && site) {
      try {

        if (this.getStatusFlag) {
          platform = await this.tunnelSvc.getPlatform({accountId: account.accountId, siteId: site.siteId, options: options});
          if (this.lowLevelFlag) {
            this.accountSvc.setPlatform(platform);
          }
          if (platform && platform.capabilities && platform.capabilities.embedded_with_pbx) {
              const lastStep = accountTags['on-board-progress'].name;
              const lastStepSucceeded = accountTags['on-board-progress'].succeeded;
              if (lastStepSucceeded && lastStep === 'site') {
                statusData.progressItem = this.gatewayProgressSvc.getNextProgressItem(lastStep);
                statusData.progressItemStatus = gatewayMessages.SYNC_PBX;
              } else if (lastStepSucceeded && lastStep === 'pbx') {
                statusData.progressItemStatus = gatewayMessages.SYNC_PBX;
              }
          }
        }
        if ( lowLevelFlag == true && this.getStatusFlag ) // we keep this call for the low level status as it might provide more information 
        {
          statusData = await this.getEcho(account, site, accountTags, platform, statusData, lowLevelFlag, options);
        }
        else if ( lowLevelFlag == false && this.getStatusFlag ) // getPlatform succeeded save the tag info
        {
          statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, true, options);
        }
      } catch (reason) {
        if ( lowLevelFlag == false )// if it was a low level status, this determination of the error would be done after the get echo call
        {
          if (this.getStatusFlag && typeof reason === 'string') {
            console.warn('getPlatform failed', reason);
            statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, false, options, `connect fail: ${reason}`);
          } else if (this.getStatusFlag && reason && reason.statusCode === 401) {
              console.warn('getPlatform failed', JSON.stringify(reason));
              const errMsg = commonMessages.TUNNEL_AUTH_ERR;
              statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, false, options, `connect fail: ${errMsg}`);
          } else if (this.getStatusFlag && reason && reason.body) {
              console.warn('getPlatform failed', JSON.stringify(reason));
              statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, false, options,
              `connect fail: ${reason.body.message && reason.body.message !== '' ? reason.body.message : 'unknown'}`);
          } else if (this.getStatusFlag && reason instanceof Error && reason.message === commonErrorMessages.AUTH_ERROR) {
              console.error('getPlatform failed', reason.message); // only log out when getting the low level status
          } else {
            console.warn('getPlatform failed', JSON.stringify(reason));
            if (this.getStatusFlag) {
              statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, false, options,
                `connect fail: ${reason ? JSON.stringify(reason) : 'unknown'}`);
              }
          }
        }
      }
    }
    return [platform, statusData];
  }

  async getEcho(account, site, accountTags, platform, statusData, lowLevelFlag: boolean, options) {
    if (account && site) {
        try {
          if (this.getStatusFlag) {
            const result = await this.tunnelSvc.getEcho({accountId: account.accountId, siteId: site.siteId, options: options});
            if (this.getStatusFlag && result.data === 'ECHO..echo..echo') {
                statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, true, options);
            } else {
              if (this.getStatusFlag) {
                console.warn('unexpected echo response', result.data);
                statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, false, options,
                  `connect fail: ${result.data && result.data !== '' ? result.data : 'unknown'}`);
              }
            }
          }
        } catch (reason) {
            if (this.getStatusFlag && typeof reason === 'string') {
                console.warn('echo failed', reason);
                statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, false, options, `connect fail: ${reason}`);
            } else if (this.getStatusFlag && reason && reason.statusCode === 401) {
                console.warn('echo failed', JSON.stringify(reason));
                const errMsg = commonMessages.TUNNEL_AUTH_ERR;
                statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, false, options, `connect fail: ${errMsg}`);
            } else if (this.getStatusFlag && reason && reason.body) {
                console.warn('echo failed', JSON.stringify(reason));
                statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, false, options,
                `connect fail: ${reason.body.message && reason.body.message !== '' ? reason.body.message : 'unknown'}`);
            } else if (this.getStatusFlag && reason instanceof Error && reason.message === commonErrorMessages.AUTH_ERROR) {
                console.error('echo failed', reason.message);
                if(lowLevelFlag){
                  await this.clHeader.logoutUser();
                }
            } else {
              console.warn('echo failed', JSON.stringify(reason));
              if (this.getStatusFlag) {
                statusData = await this.updateGatewayConnectionTag(lowLevelFlag, account, site, accountTags, platform, statusData, false, options,
                  `connect fail: ${reason ? JSON.stringify(reason) : 'unknown'}`);
                }
            }
        }
    }
    return statusData;
  }
  
  async updateGatewayConnectionTag(lowLevelFlag: boolean, account, site, accountTags, platform, statusData, connected: boolean, options, reason?: string) {
    const tagParams = {};
    let showSystemUpdateAvailable = false;

    if (platform && platform.capabilities && platform.capabilities.requires_pbx_ip_address && connected) {
      let response;

      if (this.getStatusFlag) {
        response = await this.echoSvc.getPbxStatusAccountTagsParameters(account, site, accountTags, lowLevelFlag, options );
        tagParams['pbx-status'] = response.tagParams;
      }

      if (this.getStatusFlag) {
        const gatewayConnectionTagParams = await this.echoSvc.getGatewayConnectionAccountTagsParameters(account, site, accountTags, options, 
          response.shouldGetData, response.pbxlink);
        tagParams['gateway-connection'] = gatewayConnectionTagParams;
      }
      if (this.getStatusFlag) {
        showSystemUpdateAvailable = await this.checkSystemVersions(account, site, platform, lowLevelFlag, options);
      }
    } else if (this.getStatusFlag && platform && platform.capabilities && platform.capabilities.embedded_with_pbx && connected) {
      tagParams['gateway-connection'] = { connected: connected };
      tagParams['pbx-status'] = { status: pbxStatus.UNKNOWN };
      showSystemUpdateAvailable = await this.checkSystemVersions(account, site, platform, lowLevelFlag, options );
    } else {
      tagParams['gateway-connection'] = { connected: connected, reason: reason };
      tagParams['pbx-status'] = { status: pbxStatus.UNKNOWN };
    }

    try {
      if (!accountTags) {
        accountTags = {};
      }
      let tags = accountTags;
      if (this.getStatusFlag && accountTags['gateway-connection']) {
        if (!_.isEqual(accountTags['gateway-connection'],tagParams['gateway-connection'] ))
        {
          // only update when there is a change to prevent unnecessary calls to the back end
          const tagUpdate = await this.adminSvc.updateAccountTag(account.accountId, 'gateway-connection', tagParams['gateway-connection'])
          tags = {
            ...tags, ...{ 'gateway-connection': tagUpdate }
          };
        }
      } else {
        if (this.getStatusFlag) {
          tags = await this.adminSvc.createAccountTag(account.accountId, { 'gateway-connection': tagParams['gateway-connection'] });
        }
      }

      if (this.getStatusFlag && accountTags['pbx-status']) {
        if (!_.isEqual(accountTags['pbx-status'], tagParams['pbx-status']))
        {
          // only update when there is a change to prevent unnecessary calls to the back end
          const tagUpdate = await this.adminSvc.updateAccountTag(account.accountId, 'pbx-status', tagParams['pbx-status']);
          tags = {
            ...tags, ...{ 'pbx-status': tagUpdate }
          };
        }
      } else {
        if (this.getStatusFlag) {
          tags = await this.adminSvc.createAccountTagNoFrills(account.accountId, { 'pbx-status': tagParams['pbx-status'] }); //chnage to no frills
        }
      }

      accountTags = tags;
      if (!connected) {
        statusData = this.setErrorStatus(statusData, gatewayConnectStateMessages.LOST_GATEWAY_CONNECTION);
      } else if (tags['pbx-status']['status'] === pbxStatus.DOWN) {
        statusData = this.setErrorStatus(statusData, accountTags['pbx-status']['reason']);
      // this needs to be revisited by Nikki & Suuba. There is a failing test and this method needs to be reviewed
      //  }  else if (tags['gateway-connection'] && tags['gateway-connection']['pbxConnected'] && tags['gateway-connection']['pbxConnected'] !== false) {
      } else if (tags['gateway-connection']['pbxConnected'] !== false) {
        if (showSystemUpdateAvailable) {
          statusData.statusMessage = gatewayConnectStateMessages.SYSTEM_UPDATE_AVAILABLE;
          statusData.integrationHighLevelStatus.state = 2;
        } else {
          statusData.showStep = true;
          statusData.statusMessage = statusData.progressItemStatus;
        }
      } else {
        statusData = this.setErrorStatus(statusData, tags['gateway-connection']['reason']);
      }
    } catch (error) {
      if(lowLevelFlag){
        this.errorHandlerSvc.handleError(error);
      }
      console.error('failed to update account tag', JSON.stringify(error));
    }
    return statusData;
  }
  
  setErrorStatus(statusData, msg: string) {
    statusData.integrationHighLevelStatus.state = 3; //isError
    statusData.statusMessage = msg;
    console.log("Error Status: ", msg);
    return statusData;
  }

  async checkSystemVersions(account, site, platform, lowLevelFlag: boolean, options: any) {
    // Get system versions
    let showSystemUpdateAvailable = false;
    // let options;
    let versionResponse;
    try {
      if (this.getStatusFlag) {
        versionResponse = await this.tunnelSvc.getSystemVersion({accountId: account.accountId, siteId: site.siteId, options: options});
      }
      showSystemUpdateAvailable = !this.isAllVersionsUpdated(platform, <any>versionResponse);
    } catch (error) {
      console.error('failed to get system version', JSON.stringify(error));
    }
    return showSystemUpdateAvailable;
  }

  isAllVersionsUpdated(platform, versionRes: any) {
    for(var key in versionRes) {
      if(versionRes[key] && versionRes[key]['name'] && versionRes[key].name !== '-'){
        if(versionRes[key].local_version !== '' &&
        versionRes[key].local_version !== '(unknown)' &&
        versionRes[key].local_version !== '-' &&
        versionRes[key].local_version === versionRes[key].cloud_version){
            continue;
        }
        else{
          return false;
        }
      }
    }
    if (platform && platform.capabilities && platform.capabilities.update_host) {
      if(versionRes.patch_level !== '' &&
      versionRes.patch_level !== '(unknown)' &&
      versionRes.patch_level !== '-' &&
      versionRes.patch_level === versionRes.cloud_patch_level){
        return true;
      }
      else{
        return false;
      }
    }
    else{
      return true;
    }
  }

}